import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import {
	ICloseProductCard,
	IProductCard,
	ISaveProducts,
	IShowProductCard,
	IUpdateProductPosition,
	ProductCardTab
} from '../../product/models/models';
import { ConfirmModalComponent } from '../modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from '../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../modals/confirm-modal/models/confirmModalAnswer';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ProductCardService {

	public closeSubject: Subject<void> = new Subject<void>();
	public isDirty: Map<ProductCardTab, boolean>;
	public openedProduct: number = null;
	public openSubject: ReplaySubject<void> = new ReplaySubject<void>(1);
	public saveProductsSubject: Subject<ISaveProducts> = new Subject<ISaveProducts>();
	public saveSubject: Subject<IProductCard> = new Subject<IProductCard>();
	public updatePositionSubject: Subject<IUpdateProductPosition> = new Subject<IUpdateProductPosition>();
	public updateSubject: BehaviorSubject<IShowProductCard> = new BehaviorSubject<IShowProductCard>(null);

	constructor(private dialog: MatDialog, private translate: TranslateService) {
		this.isDirty = new Map<ProductCardTab, boolean>([
			[ProductCardTab.Params, false]
		]);
	}

	get isSomeDirty(): boolean {
		const values: Array<boolean> = Array.from(this.isDirty.values());
		return values.some((value: boolean) => value);
	}

	public openSaveChangesModal(callback?: () => void): void | Observable<IConfirmModalAnswer> {
		const tabLabels: Map<ProductCardTab, string> = new Map<ProductCardTab, string>([
			[ProductCardTab.Params, 'MASTER_POSITION.INFORMATION'],
			[ProductCardTab.MasterProducts, 'MASTER_POSITION.PRODUCTS']
		]);
		const dirtyTabs: Array<string> = [];
		Array.from(this.isDirty.keys()).forEach((key: ProductCardTab) => {
			if (this.isDirty.get(key)) dirtyTabs.push(this.translate.instant(tabLabels.get(key)));
		});

		const closedEvent: Observable<IConfirmModalAnswer> = this.dialog
			.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
					width: '574px',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: this.isDirty.size === 1 ? 'MUST_SAVE_CHANGES' : 'PRODUCT_CARD.MUST_SAVE_CHANGES',
						labelTranslateParams: { tabs: dirtyTabs.join(', ') },
						cancelLabel: 'NO',
						confirmLabel: 'YES',
						twoAnswers: true
					}
				}
			)
			.afterClosed();

		if (callback) {
			closedEvent.subscribe((result: IConfirmModalAnswer) => {
				if (result.answer) callback();
			});
		} else {
			return closedEvent;
		}
	}

	public close(params: ICloseProductCard, savingSubscription?: Subscription): void {
		const close = () => {
			this.openedProduct = null;
			this.closeSubject.next();
		}

		if (savingSubscription) {
			savingSubscription.add(() => close());
			return;
		}

		if (this.isSomeDirty && !params?.force) {
			this.openSaveChangesModal(() => close());
		} else {
			close();
		}
	}

	public open(params: IShowProductCard): void {
		this.openSubject.next()

		const open = (params: IShowProductCard): void => {
			this.updateSubject.next(params);
			this.openedProduct = params.key;
		}

		if (
			this.openedProduct &&
			this.isSomeDirty &&
			this.openedProduct !== params.key
		) {
			this.openSaveChangesModal(() => open(params));
		} else {
			open(params);
		}
	}
}